var _ = require('optimizely-client-fn');
var Store = require('./base_store');

function FluxClass(config) {
  config = config || {};

  this.__stores = {} as any;

  /**
   * Mapping of storeId => array of handler functions
   */
  this.__observers = {} as any;

  this.__dispatchId = 0;

  this.__preActions = [];
  this.__postActions = [];
}

/**
 * @param {string} id
 * @param {Object} spec
 * @param {Function} spec.getInitialState
 * @param {Function} spec.initialize
 * @param {Function} spec.reset
 */
FluxClass.prototype.registerStores = function(storeMap) {
  _.forOwn(storeMap, _.bind(function(spec, id) {
    this.__stores[id] = new Store(id, this, spec);
  }, this));
};

/**
 * @param {string} id
 * @return {Store}
 */
FluxClass.prototype.getStore = function(id:string): StoreClass {
  return this.__stores[id];
};

/**
 * Dispatches an action to all stores registered
 * @param {string} actionType
 * @param {object} payload
 */
FluxClass.prototype.dispatch = function(actionType:string, payload:any) {
  if (__INVARIANT__ && !actionType) {
    throw new Error('Flux: invalid actionType ' + actionType + payload);
  }

  this.dispatchId++;

  _.each(this.__preActions, _.bind(function(preAction) {
    preAction.call(this, actionType, payload);
  }, this));

  _.forOwn(this.__stores, function(store) {
    store.__handleAction(actionType, payload);
  });

  _.each(this.__postActions, _.bind(function(postAction) {
    postAction.call(this, actionType, payload);
  }, this));

  // allow all stores to process action before notifying observers
  _.forOwn(this.__stores, _.bind(function(store, id) {
    if (store.hasChanges() && this.__observers[id]) {
      store.resetChange();
      _.each(this.__observers[id], function(handlerFn) {
        handlerFn(store);
      });
    }
  }, this));
};

/**
 * Resets all store state
 */
FluxClass.prototype.reset = function() {
  this.__observers = {} as any;
  _.forOwn(this.__stores, function(store, id) {
    store.__reset();
  });
};

/**
 * Returns a map of store => state of store
 */
FluxClass.prototype.getState = function() {
  var state = {} as any;
  _.forOwn(this.__stores, function(store, id) {
    state[id] = store.__getState();
  });
  return state;
};

/**
 * A function which, when invoked, removed a previously-added observer/handler.
 * @callback unWatchFn
 */

/**
 * @callback onAction
 * @param {string} actionType - the name of the invoked action
 * @param {*} payload - data passed to action
 * @this {Flux} Flux instance handling the action
 */

type onAction = (actionType:string, payload:any) => any;
type unWatchFn = () => void;

/**
 * Add a handler to invoke before handling an action.
 * @param {onAction} handler
 * @returns {unWatchFn}
 */
FluxClass.prototype.onPreAction = function(handler:onAction): unWatchFn {
  var handlerFns = this.__preActions;
  handlerFns.push(handler);
  return createUnwatch(handlerFns, handler);
};

/**
 * Add a handler to invoke after handling an action.
 * @param {onAction} handler
 * @returns {unWatchFn}
 */
FluxClass.prototype.onPostAction = function(handler:onAction): unWatchFn {
  var handlerFns = this.__postActions;
  handlerFns.push(handler);
  return createUnwatch(handlerFns, handler);
};

/**
 * @callback handlerCallback
 * @param {Store} arg1
 */
/**
 * Adds a handler called whenever a particular store changes
 * @param {string} storeId
 * @param {handlerCallback} handler
 * @return {unWatchFn}
 */
FluxClass.prototype.__addObserver = function(storeId:string, handler:handlerCallback): unWatchFn {
  if (!this.__observers[storeId]) {
    this.__observers[storeId] = [];
  }
  this.__observers[storeId].push(handler);
  var handlerFns = this.__observers[storeId];
  return createUnwatch(handlerFns, handler);
};

/**
 *
 * @param {Function[]} handlerFns
 * @param {Function} handler
 * @returns {unWatchFn}
 */
function createUnwatch(handlerFns:Function[], handler:Function): unWatchFn {
  return function unwatch() {
    var ind = handlerFns.indexOf(handler);
    if (ind !== -1) {
      handlerFns.splice(ind, 1);
    }
  };
}

module.exports = {
  create: function(config) {
    return new FluxClass(config);
  },
};
